import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "@/lib/queryClient";
import { seedViewAsFromHash } from "@/lib/viewAs";
import App from "./App";
import "./styles/globals.css";

// D-044 S5 (new-tab) — ПЕРШИМ: якщо вкладку відкрито як view-as (bearer у
// hash), перенести в sessionStorage цієї вкладки + почистити URL ДО render,
// щоб перший же запит (/me) уже ніс X-Kormax-View-As і рендерився як target.
seedViewAsFromHash();

try {
  if (localStorage.getItem("kormax-theme") === "dark") {
    document.documentElement.classList.add("dark");
  }
} catch {
  // localStorage blocked (Brave/Safari strict) — default to light.
}

// Double-submit CSRF cookie. Required by nginx CSRF gate on mutating verbs
// (presence check); value equality between cookie+header is enforced by the
// fetch wrapper. Per Espo's `Espo-Authorization` custom-header auth, this is
// layer-2 hardening — a stable per-session random token is sufficient.
(function ensureXsrfCookie() {
  const has = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/);
  if (has) return;
  const rnd = new Uint8Array(16);
  (globalThis.crypto ?? window.crypto).getRandomValues(rnd);
  const token = Array.from(rnd).map((b) => b.toString(16).padStart(2, "0")).join("");
  const attrs = ["path=/", "SameSite=Strict"];
  if (window.location.protocol === "https:") attrs.push("Secure");
  document.cookie = `XSRF-TOKEN=${token}; ${attrs.join("; ")}`;
})();

// A-Block A0 (HIGH-1) — funnel stock-Espo hash bookmarks into the SPA legacy
// resolver. Old links like `#Account/view/{id}` are rewritten to the path
// `/accounts/{id}`, which the router resolves via LegacyAccountRedirect →
// holding anchor (or no-access). Runs before render so React Router never sees
// the dangling hash.
(function funnelLegacyAccountHash() {
  const m = (window.location.hash || "").match(/^#Account\/view\/([A-Za-z0-9]+)/);
  if (m) {
    const base = import.meta.env.BASE_URL.replace(/\/$/, "");
    window.history.replaceState(null, "", `${base}/accounts/${m[1]}`);
  }
})();

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing #root");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);
